import { type QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AuthGate } from '../components/auth-gate';
import { openCapture } from '../components/capture/capture-store';
import { QuickCapture } from '../components/capture/quick-capture';
import { ShiftNoteMount } from '../components/shift/shift-note-mount';
import { AssistantPanel } from '../components/shell/assistant-panel';
import { CommsStrip } from '../components/shell/comms-strip';
import { setAvatars } from '../lib/avatars';
import { setSlackLinks } from '../lib/slack-links';
import { NewVersionBar } from '../components/shell/new-version';
import { Rail } from '../components/shell/rail';
import { useAssistantContext } from '../lib/assistant-context';
import { FOCUS_SEARCH, emit, isTyping } from '../lib/keys';
import { setPrefs, usePrefs } from '../lib/prefs';
import { useSession } from '../lib/auth';
import { liveProfileId, startLive, type TicketNotice } from '../lib/live';
import { liveTicketRefetch } from '../lib/live-refetch';
import { avatarsQuery, slackLinksQuery, vocabQuery } from '../lib/queries';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const { assistantOpen } = usePrefs();
  const signedIn = useSession().session?.user.id ?? null;
  const { queryClient } = Route.useRouteContext();
  // One live socket per tab while someone is signed in; a new person gets a new socket.
  useEffect(() => {
    if (!signedIn) return undefined;
    // The pickers' words, warm before C is pressed.
    queryClient.query(vocabQuery()).catch(() => undefined);
    // Slack photos for every face on the desk; initials until (or unless) they arrive.
    const loadAvatars = () => {
      queryClient
        .query({ ...avatarsQuery(), staleTime: 0 })
        .then((r) => {
          setAvatars(r.people);
        })
        .catch(() => undefined);
    };
    loadAvatars();
    queryClient
      .query(slackLinksQuery())
      .then(setSlackLinks)
      .catch(() => undefined);
    const avatarTimer = setInterval(loadAvatars, 60 * 60_000);
    const refetch = liveTicketRefetch(queryClient, { myProfileId: liveProfileId });
    const stop = startLive({ onTicket: (n: TicketNotice) => { refetch.onTicket(n); } });
    return () => {
      refetch.stop();
      stop();
      clearInterval(avatarTimer);
    };
  }, [signedIn, queryClient]);
  const context = useAssistantContext();
  const navigate = useNavigate();
  const path = useRouterState({ select: (st) => st.location.pathname });
  // On an account page, a new ticket is about that account.
  const accountId = /^\/accounts\/([0-9a-f-]{36})/.exec(path)?.[1];
  const logTicket = () => {
    openCapture(accountId ? { accountId } : {});
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTyping(e.target)) {
        if (e.key === 'Escape' && e.target instanceof HTMLElement) e.target.blur();
        return;
      }
      if (e.key === '/') {
        e.preventDefault();
        void navigate({ to: '/' }).then(() => {
          emit(FOCUS_SEARCH);
        });
      } else if (e.key === 'b' || e.key === 'B') {
        void navigate({ to: '/board' });
      } else if (e.key === 'a' || e.key === 'A') {
        setPrefs({ assistantOpen: !assistantOpen });
      } else if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        logTicket();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [assistantOpen, navigate, accountId]);

  return (
    <AuthGate>
      <div className="flex h-full flex-col">
      <NewVersionBar />
      <div className="flex min-h-0 flex-1">
        <Rail onLogTicket={logTicket} />
        <div className="flex min-w-0 flex-1 flex-col">
          <CommsStrip
            assistantOpen={assistantOpen}
            onToggleAssistant={() => {
              setPrefs({ assistantOpen: !assistantOpen });
            }}
          />
          <main className="@container/main min-h-0 flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
        {assistantOpen && <AssistantPanel context={context} />}
      </div>
      </div>
      <QuickCapture />
      <ShiftNoteMount />
    </AuthGate>
  );
}
